// ═══════════════════════════════════════════════════════════
// Форма задачі (створення й редагування), пікер клієнта і список
// користувачів для вибору виконавця.
// ═══════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { useCustomerSearch } from '../../hooks/useCustomerSearch';
import { apiFetch } from '../../services/api';
import { PRIORITY_LABEL, TYPE_LABEL, RECURRENCE_LABEL, pad, toDateInput } from './taskVocab';

/**
 * Список користувачів для вибору виконавця. Виконавець — це користувач
 * системи, а не працівник із розкладу: задачу має бути кому закрити.
 * Тягнеться раз на монтування форми й тихо деградує до «на себе».
 */
// Кеш на хвилину: форму відкривають по кілька разів на день, а список
// користувачів тенанта міняється раз на місяць. Один запит на всі форми.
let usersCache = { at: 0, promise: null };
const USERS_TTL_MS = 60_000;

// Активні проєкти для поля «Проєкт» у формі — той самий кеш на хвилину.
let projectsCache = { at: 0, promise: null };

export function useActiveProjects(enabled = true) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    if (!projectsCache.promise || Date.now() - projectsCache.at > USERS_TTL_MS) {
      projectsCache = {
        at: Date.now(),
        promise: apiFetch('/api/projects?action=list&status=active')
          .then(r => r.items || [])
          .catch(() => { projectsCache = { at: 0, promise: null }; return []; }),
      };
    }
    projectsCache.promise.then(list => { if (!cancelled) setProjects(list); });
    return () => { cancelled = true; };
  }, [enabled]);
  return projects;
}

export function useTaskUsers(enabled = true) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    if (!usersCache.promise || Date.now() - usersCache.at > USERS_TTL_MS) {
      usersCache = {
        at: Date.now(),
        promise: apiFetch('/api/users')
          .then(r => r.data || [])
          .catch(() => { usersCache = { at: 0, promise: null }; return []; }),  // пікер деградує до «на себе»
      };
    }
    usersCache.promise.then(list => { if (!cancelled) setUsers(list); });
    return () => { cancelled = true; };
  }, [enabled]);
  return users;
}

/**
 * Клієнт для задачі. Через наявний useCustomerSearch — клієнтів у тенанті
 * можуть бути десятки тисяч, тож список ніхто не тримає в памʼяті.
 */
export function CustomerPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const { results, loading } = useCustomerSearch(q, { enabled: open });

  if (value) {
    return (
      <div className="tasks-picked">
        <span>{value.name}{value.phone ? ` · ${value.phone}` : ''}</span>
        <button type="button" className="tasks-picked-clear" onClick={() => onChange(null)}>✕</button>
      </div>
    );
  }
  return (
    <div className="tasks-picker">
      <input
        type="text" value={q} placeholder="Почніть вводити імʼя або телефон"
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && q.trim() && (
        <div className="tasks-picker-list">
          {loading && <div className="tasks-picker-note">Шукаю…</div>}
          {!loading && !results.length && <div className="tasks-picker-note">Нічого не знайдено</div>}
          {results.map(c => (
            <button
              key={c.id} type="button" className="tasks-picker-item"
              onClick={() => { onChange({ id: c.id, name: c.name, phone: c.phone }); setOpen(false); setQ(''); }}
            >
              <span>{c.name}</span>
              <span className="tasks-picker-phone">{c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Одна форма на створення й редагування — правила однакові, тож і поля теж.
 *
 * `lockedCustomer` ставить клієнта наперед і ховає пікер: із картки клієнта
 * задачу заводять саме на нього, і давати там вибрати іншого — це запросити
 * помилку, а не дати свободу.
 */
export function TaskFormModal({
  task, users, canAssign, currentUser, userProfile, activeLocationId,
  lockedCustomer = null, defaults = null, onClose, onSaved,
}) {
  const toast = useToast();
  const editing = !!task;
  const due = task?.dueAt ? new Date(task.dueAt) : null;

  const [form, setForm] = useState({
    title: task?.title || defaults?.title || '',
    description: task?.description || '',
    type: task?.type || defaults?.type || 'task',
    priority: task?.priority || 'normal',
    assigneeUid: task?.assigneeUid || currentUser.uid,
    dueDate: due ? toDateInput(due.getTime()) : (defaults?.dueDate || ''),
    dueTime: due ? `${pad(due.getHours())}:${pad(due.getMinutes())}` : (defaults?.dueTime || '18:00'),
    expectedAmount: task?.expectedAmount || '',
    recurrenceRule: task?.recurrenceRule || '',
    projectId: task?.projectId || defaults?.projectId || '',
  });
  const projects = useActiveProjects();
  const [customer, setCustomer] = useState(
    lockedCustomer
      || (task?.customerId ? { id: task.customerId, name: task.customerName, phone: task.customerPhone } : null)
  );
  const [checklist, setChecklist] = useState(task?.checklist || []);
  const [newItem, setNewItem] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addItem = () => {
    const title = newItem.trim();
    if (!title) return;
    setChecklist(list => [...list, { id: `k_${Date.now()}`, title, isDone: false }]);
    setNewItem('');
  };

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error('Назва — щонайменше 3 символи'); return; }
    // Сервер відмовить у задачі на продаж без клієнта — краще сказати це
    // тут, ніж дати людині заповнити форму й отримати 422.
    if (form.type === 'sale' && !customer) {
      toast.error('Задача на продаж потребує клієнта');
      return;
    }
    setBusy(true);
    try {
      const assignee = users.find(u => u.id === form.assigneeUid);
      const body = {
        title: form.title.trim(),
        description: form.description || undefined,
        type: form.type,
        priority: form.priority,
        assigneeUid: form.assigneeUid,
        assigneeName: assignee?.displayName || assignee?.email
          || (form.assigneeUid === currentUser.uid
            ? (userProfile?.displayName || currentUser.email) : null) || null,
        dueAt: form.dueDate ? new Date(`${form.dueDate}T${form.dueTime || '18:00'}`).getTime() : null,
        customerId: customer?.id || null,
        customerName: customer?.name || null,
        customerPhone: customer?.phone || null,
        expectedAmount: form.type === 'sale' && form.expectedAmount ? Number(form.expectedAmount) : null,
        recurrenceRule: form.recurrenceRule || null,
        checklist,
        projectId: form.projectId || null,
      };
      let r;
      if (editing) {
        // Лише змінене (аудит 2026-11-03): форма — знімок на момент
        // відкриття, і повний набір полів затирав те, що інші встигли
        // змінити тим часом, — галочки виконавця в чеклісті, передачу
        // задачі. Дедлайн тут не міняється — для цього «Перенести».
        const original = {
          title: task.title, description: task.description || undefined, type: task.type,
          priority: task.priority, assigneeUid: task.assigneeUid,
          customerId: task.customerId || null, expectedAmount: task.expectedAmount ?? null,
          recurrenceRule: task.recurrenceRule || null, checklist: task.checklist || [],
          projectId: task.projectId || null,
        };
        const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
        const changes = {};
        for (const k of Object.keys(original)) {
          if (!same(body[k], original[k])) changes[k] = body[k];
        }
        if (changes.assigneeUid !== undefined) changes.assigneeName = body.assigneeName;
        if (changes.customerId !== undefined) {
          changes.customerName = body.customerName;
          changes.customerPhone = body.customerPhone;
        }
        if (!Object.keys(changes).length) { onClose(); return; }
        r = await apiFetch('/api/tasks/update', { method: 'POST', body: { id: task.id, ...changes } });
      } else {
        r = await apiFetch('/api/tasks/create', { method: 'POST', body: { ...body, locationId: activeLocationId } });
      }
      toast.success(editing ? 'Збережено' : 'Задачу створено');
      onSaved(r.task);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося зберегти задачу');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--task" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <h2>{editing ? 'Редагувати задачу' : 'Нова задача'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-field">
            <label className="field-label">Що зробити</label>
            <input type="text" autoFocus value={form.title}
              placeholder="Передзвонити Ользі щодо абонемента"
              onChange={e => set('title', e.target.value)} />
          </div>
          <div className="tasks-form-row">
            <div className="form-field">
              <label className="field-label">Виконавець</label>
              <select value={form.assigneeUid} disabled={!canAssign}
                onChange={e => set('assigneeUid', e.target.value)}>
                <option value={currentUser.uid}>Я</option>
                {users.filter(u => u.id !== currentUser.uid).map(u => (
                  <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label className="field-label">Пріоритет</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="tasks-form-row">
            <div className="form-field">
              <label className="field-label">Дата</label>
              <input type="date" value={form.dueDate} disabled={editing}
                title={editing ? 'Дедлайн змінюється кнопкою «Перенести» у задачі' : undefined}
                onChange={e => set('dueDate', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">Час</label>
              <input type="time" value={form.dueTime} disabled={editing}
                title={editing ? 'Дедлайн змінюється кнопкою «Перенести» у задачі' : undefined}
                onChange={e => set('dueTime', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">Повторюваність</label>
              <select value={form.recurrenceRule} onChange={e => set('recurrenceRule', e.target.value)}>
                <option value="">Не повторюється</option>
                {Object.entries(RECURRENCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          {form.recurrenceRule && !form.dueDate && (
            <p className="tasks-hint">Повторення рахується від дедлайну — без дати наступний екземпляр не створиться.</p>
          )}
          <div className="tasks-form-row">
            <div className="form-field">
              <label className="field-label">Тип</label>
              <select value={form.type} onChange={e => set('type', e.target.value)}>
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {form.type === 'sale' && (
              <div className="form-field">
                <label className="field-label">Очікувана сума, ₴</label>
                <input type="number" value={form.expectedAmount}
                  onChange={e => set('expectedAmount', e.target.value)} />
              </div>
            )}
          </div>
          <div className="form-field">
            <label className="field-label">
              Клієнт {form.type === 'sale' && <span className="tasks-req">обовʼязково</span>}
            </label>
            {lockedCustomer ? (
              <div className="tasks-picked">
                <span>{lockedCustomer.name}{lockedCustomer.phone ? ` · ${lockedCustomer.phone}` : ''}</span>
              </div>
            ) : (
              <CustomerPicker value={customer} onChange={setCustomer} />
            )}
          </div>

          {/* Поле є, лише коли є куди класти: тенант без проєктів його не бачить. */}
          {(projects.length > 0 || form.projectId) && (
            <div className="form-field">
              <label className="field-label">Проєкт</label>
              <select value={form.projectId} onChange={e => set('projectId', e.target.value)}>
                <option value="">Без проєкту</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                {form.projectId && !projects.some(p => p.id === form.projectId) && (
                  <option value={form.projectId}>(поточний проєкт)</option>
                )}
              </select>
            </div>
          )}
          <div className="form-field">
            <label className="field-label">Опис</label>
            <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="form-field">
            <label className="field-label">Чекліст</label>
            {checklist.map((c, i) => (
              <div key={c.id || i} className="tasks-check-edit">
                <span>{c.title}</span>
                <button type="button" onClick={() => setChecklist(l => l.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <div className="tasks-check-add">
              <input
                type="text" value={newItem} placeholder="Додати пункт"
                onChange={e => setNewItem(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
              />
              <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}>+</button>
            </div>
          </div>

          {form.type === 'sale' && (
            <div className="tasks-hint">
              Задачу на продаж не можна закрити без результату — доведеться вказати,
              продано чи ні, і на яку суму.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Скасувати</button>
          <button className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? 'Зачекайте…' : (editing ? 'Зберегти' : 'Створити')}
          </button>
        </div>
      </div>
    </div>
  );
}
